// API Base URL
const API_BASE_URL ="https://campusproapi.ainsoft.in/";
const getUploadApiBaseUrl = () => API_BASE_URL.replace(/\/$/, '');

// Get token from localStorage
const getToken = () => {
  const userData = localStorage.getItem('userData');
  if (userData) {
    try {
      const parsed = JSON.parse(userData);
      return parsed.token || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Helper function to make API calls
const apiCall = async (endpoint, method = 'GET', data = null) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = getToken();
  
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
  };

  // Add token to headers if available
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }

  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

// Auth APIs
export const authService = {
  login: async (userName, password) => {
    const url = `${API_BASE_URL}/Login`;
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          UserName: userName,
          Password: password
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // API returns null if login fails
      return data;
    } catch (error) {
      console.error('Login API error:', error);
      throw error;
    }
  },
  logout: async () => {
    try {
      const token = getToken();
      if (token) {
        await fetch(`${API_BASE_URL}LogOut`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Token: token })
        });
      }
    } catch (e) {
      console.warn('Logout API failed:', e);
    } finally {
      localStorage.removeItem('userData');
    }
  },
};

// College APIs
export const collegeService = {
  getColleges: () => apiCall('/GetColleges', 'GET'),
  saveCollege: (data) => apiCall('/SaveCollege', 'POST', data),
  deleteCollege: (id) => apiCall(`/DeleteCollege/${id}`, 'GET'),
};

// Company APIs
export const companyService = {
  getCompanies: () => apiCall('/GetCompanies', 'GET'),
  saveCompany: (data) => apiCall('/SaveCompany', 'POST', data),
  deleteCompany: (id) => apiCall(`/DeleteCompany/${id}`, 'GET'),
};

// Coordinator APIs
export const coordinatorService = {
  getCoordinators: () => apiCall('/GetCordinators', 'GET'),
  saveCoordinator: (data) => apiCall('/SaveCordinator', 'POST', data),
  deleteCoordinator: (id) => apiCall(`/DeleteCordinator/${id}`, 'GET'),
};

// Country APIs
export const countryService = {
  getCountries: () => apiCall('/GetCountries', 'GET'),
  saveCountry: (data) => apiCall('/SaveCountry', 'POST', data),
  deleteCountry: (id) => apiCall(`/DeleteCountry/${id}`, 'GET'),
};

// District APIs
export const districtService = {
  getDistricts: () => apiCall('/GetDistricts', 'GET'),
  saveDistrict: (data) => apiCall('/SaveDistrict', 'POST', data),
  deleteDistrict: (id) => apiCall(`/DeleteDistrict/${id}`, 'GET'),
};

// State APIs
export const stateService = {
  getStates: () => apiCall('/GetStates', 'GET'),
  saveState: (data) => apiCall('/SaveState', 'POST', data),
  deleteState: (id) => apiCall(`/DeleteState/${id}`, 'GET'),
};

// Student APIs
export const studentService = {
  getStudents: () => apiCall('/GetStudents', 'GET'),
  getStudentById: (id) => apiCall(`/GetStudent/${id}`, 'GET'),
  saveStudent: (data) => apiCall('/SaveStudent', 'POST', data),
  deleteStudent: (id) => apiCall(`/DeleteStudent/${id}`, 'GET'),
  getPendingStudentsByCollege: (collegeId) => apiCall(`/GetPendingStudentsByCollege/${collegeId}`, 'GET'),
  approveStudent: (studentId, coordinatorId) => apiCall(`/ApproveStudent/${studentId}?coordinatorId=${coordinatorId || ''}`, 'POST'),
  saveStudentWithMarksheet: async (formData) => {
    const url = `${getUploadApiBaseUrl()}/SaveStudentWithMarksheet`;
    const options = { method: 'POST', body: formData };
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(response.statusText || 'Failed');
    return await response.json();
  },
  getMarksheetDownloadUrl: (studentId) => {
    return `${getUploadApiBaseUrl()}/DownloadMarksheet/${studentId}`;
  },
  downloadMarksheet: async (studentId, filename = 'marksheet.pdf') => {
    const url = `${getUploadApiBaseUrl()}/DownloadMarksheet/${studentId}`;
    const token = getToken();
    const options = { method: 'GET' };
    if (token) options.headers = { Authorization: `Bearer ${token}` };
    const response = await fetch(url, options);
    if (!response.ok) throw new Error('Failed to download');
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  },
};

// Notification APIs
export const notificationService = {
  getByUser: (userId) => apiCall(`/GetNotificationsByUser/${userId}`, 'GET'),
  markRead: (id) => apiCall(`/MarkNotificationRead/${id}`, 'POST'),
};

// Application APIs (multipart upload - can't use apiCall)
export const applicationService = {
  getApplications: () => apiCall('/GetApplications', 'GET'),
  deleteApplication: (id) => apiCall(`/DeleteApplication/${id}`, 'GET'),
  saveApplicationWithResume: async (formData) => {
    const url = `${getUploadApiBaseUrl()}/SaveApplicationWithResume`;
    const token = getToken();
    const options = {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData
    };
    const response = await fetch(url, options);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return await response.json();
  },
  getApplicationsByStudent: (studentId) => apiCall(`/GetApplicationsByStudent/${studentId}`, 'GET'),
  getApplicationsByCompany: (companyId) => apiCall(`/GetApplicationsByCompany/${companyId}`, 'GET'),
  getApplicationsByCollege: (collegeId) => apiCall(`/GetApplicationsByCollege/${collegeId}`, 'GET'),
  updateApplicationReview: (data) => apiCall('/UpdateApplicationReview', 'POST', data),
  downloadResume: async (applicationId, filename = 'resume.pdf') => {
    const url = `${getUploadApiBaseUrl()}/DownloadResume/${applicationId}`;
    const token = getToken();
    const options = { method: 'GET' };
    if (token) options.headers = { Authorization: `Bearer ${token}` };
    const response = await fetch(url, options);
    if (!response.ok) throw new Error('Failed to download');
    const blob = await response.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'resume.pdf';
    a.click();
    URL.revokeObjectURL(a.href);
  },
};

// University APIs
export const universityService = {
  getUniversities: () => apiCall('/GetUniversities', 'GET'),
  saveUniversity: (data) => apiCall('/SaveUniversity', 'POST', data),
  deleteUniversity: (id) => apiCall(`/DeleteUniversity/${id}`, 'GET'),
};

// PlacementDrive APIs
export const placementDriveService = {
  getPlacementDrives: () => apiCall('/GetPlacementDrives', 'GET'),
  GetNewPlacementDrives: () => apiCall('/GetNewPlacementDrives', 'GET'),
  GetPlacementDrivesByCollege: (id) => apiCall(`/GetPlacementDrivesByCollege/${id}`, 'GET'),
  GetNewPlacementDrivesByCollege: (id) => apiCall(`/GetNewPlacementDrivesByCollege/${id}`, 'GET'),
  savePlacementDrive: (data) => apiCall('/SavePlacementDrive', 'POST', data),
  deletePlacementDrive: (id) => apiCall(`/DeletePlacementDrive/${id}`, 'GET'),
};

// Vacancy APIs
export const vacancyService = {
  getVacancies: () => apiCall('/GetVacancies', 'GET'),
  getVacanciesByPlacement: (id) => apiCall(`/GetVacanciesByPlacement/${id}`, 'GET'),
  getVacanciesByCompany: (id) => apiCall(`/GetVacanciesByCompany/${id}`, 'GET'),
  getVacanciesByCollege: (id) => apiCall(`/GetVacanciesByCollege/${id}`, 'GET'),
  saveVacancy: (data) => apiCall('/SaveVacancy', 'POST', data),
  deleteVacancy: (id) => apiCall(`/DeleteVacancy/${id}`, 'GET'),
};

